/**
 * `clearUserStorage` — removes all user-scoped localStorage / sessionStorage
 * entries on logout, relocated from the frontend into the SDK as part of the
 * auth-cutover (spec 009, FILE 14, Group B/C).
 *
 * Platform consideration: this function accesses `localStorage` and
 * `sessionStorage` directly. It is SSR-safe (`typeof window === 'undefined'`
 * guard) and always no-ops on React Native (no `window` global). It is NOT
 * routed through the `PlatformAdapter.storage` slot because:
 *   1. Logout is a one-shot imperative action, not a hot-path hook.
 *   2. The operation is multi-key with prefix scanning — the adapter slot
 *      does not expose `localStorage.length` or `localStorage.key(i)`.
 *   3. React Native doesn't run the logout flow that calls this function
 *      (RN auth teardown has its own sequence via the native secure store).
 *
 * Keys that are intentionally preserved across accounts:
 * - preferred-locale  (user's language choice)
 * - preferred-currency (user's currency choice)
 * - theme-override (light/dark preference)
 */

/** localStorage keys to remove on logout. */
const LOCAL_STORAGE_KEYS: string[] = [
  // Onboarding — ONLY the unscoped mirrors. The user-scoped variants
  // (`onboarding-completed:{uid}`, `onboarding-stage:{uid}`,
  // `onboarding-persist-pending:{uid}`) deliberately SURVIVE logout: they are
  // only ever read back when the SAME user id is active again, so keeping
  // them lets a returning user resume onboarding at the stage they reached
  // (and lets the completion backend-write retry) instead of restarting from
  // stage 1 (MOR-1784). The unscoped mirrors are the cross-user leak risk on
  // a shared browser — those are wiped.
  'onboarding-completed',
  'onboarding-stage',
  'onboarding-persist-pending',
  // Active wallet selection
  'morfi-active-wallet',
  // Debate sort preference (user-specific context)
  'morfi:debate-sort',
  // Proposal feed filters
  'morfi:proposalFeedFilters',
  // Bearer token (session auth fallback — shared across tabs via localStorage)
  'morfi_bearer_token',
  // User ID (shared across tabs via localStorage)
  'morfi_user_id',
  // Session deadline learned by the web refresh scheduler
  'morfi_session_expires_at',
];

/** localStorage prefixes to remove on logout (removes all matching keys). */
const LOCAL_STORAGE_PREFIXES: string[] = [
  // Leaderboard rank cache
  'morfi:your-rank:',
];

/** sessionStorage keys to remove on logout. */
const SESSION_STORAGE_KEYS: string[] = ['onboarding-dismissed'];

/**
 * Remove all user-scoped storage. Safe to call in SSR (no-ops) AND on
 * React Native (no-ops — RN has no `localStorage`/`sessionStorage`).
 *
 * Guard explanation: modern React Native polyfills `window` to be an
 * object, so `typeof window === 'undefined'` does NOT correctly skip on
 * RN — it falls through and crashes on the first `localStorage.removeItem`
 * call. Check for `localStorage` directly instead. The RN auth provider
 * does its own MMKV cleanup of `morfi_user_id` / `morfi_bearer_token`
 * separately (see `auth-provider.tsx` logout path).
 *
 * Drop-in replacement for the old `frontend/src/lib/auth/cleanup.ts`.
 * Import from `@morfi/backend-sdk-v2/client`.
 */
export function clearUserStorage(): void {
  if (typeof localStorage === 'undefined') return;

  // localStorage — exact keys
  for (const key of LOCAL_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }

  // localStorage — prefix matches
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key !== null && LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }

  // sessionStorage — exact keys
  for (const key of SESSION_STORAGE_KEYS) {
    sessionStorage.removeItem(key);
  }
}
